/* eslint-disable react/button-has-type */
/* eslint-disable jsx-a11y/control-has-associated-label */
import classNames from "classnames";
import Lottie from "lottie-react";
import { memo, useCallback, useEffect, useState } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";

// Constants
import { GET_PARAMS, GET_POPUP_TYPES } from "constants/popupConfig";

// Containers
import ButtonFavoriteContainer from "containers/atoms/ButtonFavoriteContainer";

// JSON
import BangerPreloader from "assets/json/banger_game_preloader.json";
import GloryPreloader from "assets/json/glory_game_preloader.json";

// Hooks
import { useLoadGame } from "hooks/useLoadGame";
import useTranslate from "hooks/useTranslate";
import { saveLastGame } from "hooks/useBackToGame";
import { useRouterHistoryStore } from "stores/routerHistory";

// Icons
import BangerLogo from "assets/icons/banger/logo-no-icon.svg?react";
import ArrowBackIcon from "assets/icons/glory/arrow-back.svg?react";
import GloryLogo from "assets/icons/glory/glory_logo_mini.svg?react";
import * as authSelectors from "redux/auth/selectors";
import { useCheckBalance } from "hooks/useCheckBalance";

import { useSelector } from "react-redux";
// Types
import { GAME_MODE, GameType } from "types/Games";

import "./styles.sass";
import { useAuthStore } from "stores/auth";
import { checkCountry } from "utils/checkVPN";
import { VPNNotification } from "components/atoms/VPNNotification";
import * as userSelectors from "redux/user/selectors";
import { makeLangUrl } from "../../../../helpers/makeLangUrl";
import url from "../../../../url";

type GamePageMobileProps = {
  gameUrl: string;
  mode: GAME_MODE;
  gameAlias: string;
  reset: () => any;
  currentGame: GameType | null;
};

const GamePageMobile = (props: GamePageMobileProps) => {
  const { currentGame, gameUrl, mode, gameAlias, reset } = props;
  const t = useTranslate();
  const location = useLocation();
  const previousPage = useRouterHistoryStore((state) => state.previous);
  const navigate = useNavigate();
  const [isLoaderProcess, setIsLoaderProcess] = useState(false);
  const [isVPN, setIsVPN] = useState(false);
  const [loadGame, { isLoading, isError }] = useLoadGame();
  const checkBalance = useCheckBalance();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const userData = useSelector(userSelectors.all);

  const { VITE_APP_PROJECT_NAME } = import.meta.env;

  const isAuthCheck = useAuthStore((state) => state.isAfterAuthCheck);
  const handleCheckVPN = async () => {
    const VPNCheck = await checkCountry(userData);
    setIsVPN(VPNCheck);
  };
  useEffect(() => {
    if (mode !== GAME_MODE.DEMO) {
      if (isAuthCheck) {
        const isBalancePositive = checkBalance();
        if (isBalancePositive) {
          loadGame(mode, gameAlias);
        }
      }
    } else {
      loadGame(mode, gameAlias);
    }

    return () => reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthCheck, mode, isAuthenticated]);

  useEffect(() => {
    if (
      !isLoading &&
      (currentGame?.provider?.name === "Evolution" ||
        currentGame?.provider?.name === "Evolution2")
    ) {
      handleCheckVPN();
      setTimeout(() => {
        setIsVPN(false);
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, currentGame]);

  useEffect(() => {
    if (isError) {
      navigate(makeLangUrl(url.routes.casino.index()));
    }
  }, [isError, navigate]);
  useEffect(() => {
    setTimeout(() => {
      setIsLoaderProcess(true);
    }, 2000);
  }, []);

  const generateCoverPath = useCallback((): string => {
    if (!currentGame) {
      return "";
    }
    const providerName = currentGame.provider.name.toLowerCase();
    const isGlory = VITE_APP_PROJECT_NAME === "glory";
    const isBanger = VITE_APP_PROJECT_NAME === "banger";
    if (providerName === "inout") {
      if (isGlory) {
        return `https://diswdgcu9cfva.cloudfront.net/game-covers/glory/${currentGame.urlAlias}.webp`;
      }
      if (isBanger) {
        return `https://diswdgcu9cfva.cloudfront.net/game-covers/banger/${currentGame.urlAlias}.webp`;
      }
    }

    return `https://diswdgcu9cfva.cloudfront.net/covers2/${currentGame.urlAlias}.webp`;
  }, [currentGame, VITE_APP_PROJECT_NAME]);

  useEffect(() => {
    if (isAuthenticated) {
      saveLastGame({
        title: currentGame?.name ?? "",
        cover: generateCoverPath(),
        link: location.pathname,
        gameId: currentGame?.uuid ?? "",
      });
    }
  }, [currentGame, gameUrl, isAuthenticated, generateCoverPath, location]);

  const handleGoBack = () => {
    navigate(previousPage);
  };

  const handleDeposit = () => {
    navigate({
      search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.deposit })}`,
    });
  };

  const logo =
    VITE_APP_PROJECT_NAME === "banger" ? <BangerLogo /> : <GloryLogo />;

  const pageClass = classNames("GamePageMobile", {
    banger: VITE_APP_PROJECT_NAME === "banger",
  });

  const preloader =
    VITE_APP_PROJECT_NAME === "banger" ? BangerPreloader : GloryPreloader;

  return (
    <div className={pageClass}>
      {isLoading || !isLoaderProcess ? (
        <div className="GamePageMobile__loader">
          <Lottie animationData={preloader} loop={false} />
        </div>
      ) : (
        <>
          <VPNNotification isVPN={isVPN} />
          <iframe
            src={gameUrl}
            className={`GamePageMobile__iframe${isVPN ? "_blurred" : ""}`}
            title="GamePageMobile"
          />
        </>
      )}

      <div className="GamePageMobile__panel">
        <div className="GamePageMobile__panel_left">
          <button className="GamePageMobile__back" onClick={handleGoBack}>
            <ArrowBackIcon className="GamePageMobile__back_icon" />
          </button>
          <div className="GamePageMobile__logo" onClick={handleGoBack}>
            {logo}
          </div>
        </div>
        <div className="GamePageMobile__panel_right">
          <div className="GamePageMobile-deposit">
            <button
              type="button"
              className="GamePageMobile-deposit__btn"
              onClick={handleDeposit}
            >
              {t("profile.balance.deposit.title")}
            </button>
          </div>
          <div className="GamePageMobile__panel_right_favorite">
            <ButtonFavoriteContainer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(GamePageMobile);
